import { FormRenderProps } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect } from 'react';

import { checkQuillEditorDeltaIsEmpty } from 'CorePackage/MainModule/validators/isRequiredQuillEditorDelta';

import { IAdminSetting } from '../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { IKnmPmForm } from '../interfaces/IKnmPmForm';
import { IKnmForm } from '../../../KnmPackage/MainModule/interfaces/IKnmForm';
import {
  getKnmFormRef,
  getKnmIsErknmStatusFillInProcess,
  getKnmIsErknmStatusTypeWaitingCarryOut,
  getKnmSystemType,
} from '../../../KnmPackage/MainModule/selectors/knmSelectors';
import { ProfKindCodesEnum } from '../../../APIPackage/DictionariesModule/enums/ProfKindCodesEnum';
import { IKnmFormRef } from '../../../KnmPackage/MainModule/interfaces/IKnmFormRef';
import { KnmSystemTypesEnum } from '../../../KnmPackage/MainModule/enums/KnmSystemTypesEnum';
import { IErknmStatusHintField } from '../../../KnmPackage/MainModule/interfaces/IErknmStatusHintField';
import { getErrorFieldDataByFlatName } from '../../../KnmPackage/MainModule/common/getErrorFieldDataByFlatName';
import { isFieldRequiredForProfVisitStatusTypeCarriedOut } from '../common/fieldsRequiredForProfVisitStatusTypeCarriedOut';
import { getIsPmPaperlessActEnabled } from '../common/getIsPmPaperlessActEnabled';
import { knmPmSetStatusHintFields, knmPmSetStatusHintText } from '../actions/knmPmActions';
import { knmSetStatusHintFields, knmSetStatusHintText } from '../../../KnmPackage/MainModule/actions/knmActions';
import { isFieldsRequiredForAnnouncementOfWarningRemark } from '../common/fieldsRequiredForAnnouncementOfWarningRemark';
import { getLawInterface } from '../../../CorePackage/MainModule/reducers/coreReducer';
import { LawInterfacesEnum } from '../../../CorePackage/MainModule/enums/LawInterfacesEnum';
import { KnmVersionsEnum } from '../../../APIPackage/KnmModule/enums/KnmVersionsEnum';
import { isFieldsRequiredForProfVisitStatusTypeCompleted } from '../common/fieldsRequiredForProfVisitStatusTypeCompleted';
import { IKnmPmFormRequirementsNpa } from '../interfaces/IKnmPmFormRequirementsNpa';
import { isLaterThanAdminSetting } from '../../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from '../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';

type FormRenderType = FormRenderProps<IKnmForm>;

interface IUseKnmPmFormStatusHintInitParams {
  values: IKnmPmForm;
  touched: FormRenderType['touched'];
  settings: IAdminSetting[] | undefined;
}

const getEmptyMarkedFields = (
  markedFields: string[],
  formRef: IKnmFormRef | undefined,
  knmSystemType: KnmSystemTypesEnum | undefined,
  created: string | null | undefined,
  settings: IAdminSetting[] | undefined,
): IErknmStatusHintField[] => {
  const result: IErknmStatusHintField[] = [];
  const emptyMarkedFields = markedFields.filter(item => {
    const value = formRef?.current.getFieldState(item as keyof IKnmForm)?.value;

    if (item.includes('noteWarningReactQuillEditor')) {
      const isEmpty = checkQuillEditorDeltaIsEmpty(value);

      return isEmpty;
    }

    return !value || (Array.isArray(value) && !value.length);
  });

  emptyMarkedFields.forEach(item => {
    const fieldData =
      formRef && knmSystemType
        ? getErrorFieldDataByFlatName(item, formRef, knmSystemType, created, settings)
        : undefined;

    if (fieldData?.title) {
      result.push({ scrollToId: fieldData.scrollElementId, title: fieldData.title, field: item });
    }
  });

  return result;
};

const hasIncompleteRequirementsNpa = (
  requirementsNpa: IKnmPmFormRequirementsNpa[] | undefined,
  created: string | null | undefined,
  settings: IAdminSetting[] | undefined,
): boolean => {
  if (!requirementsNpa?.length) {
    return false;
  }

  if (
    !created ||
    !isLaterThanAdminSetting(created, settings, AdminSettingCodesEnum.NPA_VALIDATION_ERVK_ROT_4_1_24_KNM_STARTS_FROM)
  ) {
    return false;
  }

  return requirementsNpa.some(item => !item.mandatory?.length);
};

export function useKnmPmFormStatusHintInit({ values, touched, settings }: IUseKnmPmFormStatusHintInitParams) {
  const dispatch = useDispatch();
  // const isSupervisor = useSelector(getUserRoleIsSupervisor);
  // const isProsecutorOrSuperProsecutor = useSelector(getUserRoleIsProsecutorOrSuperProsecutor);
  const isStatusFillInProcess = useSelector(getKnmIsErknmStatusFillInProcess);
  const isStatusTypeWaitingCarryOut = useSelector(getKnmIsErknmStatusTypeWaitingCarryOut);
  const knmSystemType = useSelector(getKnmSystemType);
  const lawInterface = useSelector(getLawInterface);
  const formRef = useSelector(getKnmFormRef);

  const pmTypeCode = values.pmType?.code;
  const isProfessionalVisit = pmTypeCode === ProfKindCodesEnum.PP;

  // для профвизита переход в статус TYPE_CARRIED_OUT
  const updateProfessionalVisitTypeCarriedOut = useCallback(() => {
    const markedFields = touched
      ? Object.keys(touched).filter(item => isFieldRequiredForProfVisitStatusTypeCarriedOut(item, values))
      : [];

    const emptyMarkedFields = getEmptyMarkedFields(markedFields, formRef, knmSystemType, values.created, settings);

    const isAlreadyMarked = emptyMarkedFields.some(field => field.field === 'requirementsNpa');

    if (!isAlreadyMarked && hasIncompleteRequirementsNpa(values.requirementsNpa, values.created, settings)) {
      const fieldData =
        formRef && knmSystemType
          ? getErrorFieldDataByFlatName('requirementsNpa', formRef, knmSystemType, values.created, settings)
          : undefined;

      if (fieldData) {
        emptyMarkedFields.push({
          scrollToId: fieldData.scrollElementId,
          title: fieldData.title ?? 'Обязательные требования, подлежащие проверке',
          field: 'requirementsNpa',
        });
      }
    }

    const text = emptyMarkedFields.length
      ? `Для перехода в статус “Ожидает проведения” необходимо заполнить указанные сведения и подписать паспорт:`
      : `Для перехода в статус “Ожидает проведения” необходимо подписать паспорт`;

    dispatch(knmPmSetStatusHintText(text));
    dispatch(knmPmSetStatusHintFields(emptyMarkedFields));
  }, [touched, values, settings, knmSystemType, formRef]);

  // для профвизита переход в статус "завершено"
  const updateProfessionalVisitTypeCompleted = useCallback(() => {
    // акт есть на форме только в безбумажном режиме, иначе результат вносят
    // в раздел "Сведения о результатах профилактического мероприятия"
    const isActSectionVisible =
      Boolean(values.isUnableToRefuse) && getIsPmPaperlessActEnabled(values.created, settings);

    const markedFields = touched
      ? Object.keys(touched).filter(item => isFieldsRequiredForProfVisitStatusTypeCompleted(item, values))
      : [];

    let isEmptySubjectNotifications = false;

    const emptyMarkedFields = getEmptyMarkedFields(markedFields, formRef, knmSystemType, values.created, settings).map(
      markedField => {
        if (markedField.field === 'act') {
          return {
            ...markedField,
            title: isActSectionVisible ? 'Сведения об акте' : 'Результат профилактического мероприятия',
          };
        }
        if (markedField.field === 'subjectNotifications') {
          isEmptySubjectNotifications = true;
        }

        return markedField;
      },
    );

    if (isEmptySubjectNotifications) {
      const text = emptyMarkedFields.length
        ? `Для перехода в статус “Завершено” необходимо заполнить указанные сведения и подписать паспорт:`
        : `Для перехода в статус “Завершено” необходимо подписать паспорт`;

      dispatch(knmPmSetStatusHintText(text));
      dispatch(knmPmSetStatusHintFields(emptyMarkedFields));
    } else {
      const text = isActSectionVisible
        ? 'Для перехода в статус “Завершено“ необходимо заполнить раздел “Сведения об акте“ и подписать акт'
        : 'Для перехода в статус “Завершено“ необходимо заполнить раздел “Результат профилактического мероприятия“ и подписать паспорт';

      dispatch(knmPmSetStatusHintText(text));
      dispatch(knmPmSetStatusHintFields([]));
    }
  }, [values, settings]);

  const updateAnnouncementOfWarningRemark = useCallback(() => {
    const markedFields = touched
      ? Object.keys(touched).filter(item =>
          isFieldsRequiredForAnnouncementOfWarningRemark(
            item,
            values,
            lawInterface === LawInterfacesEnum.FZ_248 ? KnmVersionsEnum.ERKNM : KnmVersionsEnum.ERP,
          ),
        )
      : [];

    const emptyMarkedFields = getEmptyMarkedFields(markedFields, formRef, knmSystemType, values.created, settings);

    const text = emptyMarkedFields.length
      ? `Для перехода в статус “Предостережение объявлено” необходимо заполнить указанные сведения и подписать паспорт:`
      : `Для перехода в статус “Предостережение объявлено” необходимо подписать паспорт`;

    dispatch(knmPmSetStatusHintText(text));
    dispatch(knmPmSetStatusHintFields(emptyMarkedFields));
  }, [touched, values, settings, knmSystemType, formRef]);

  useEffect(() => {
    if (isStatusFillInProcess && isProfessionalVisit) {
      updateProfessionalVisitTypeCarriedOut();
    } else if (isStatusTypeWaitingCarryOut && isProfessionalVisit) {
      updateProfessionalVisitTypeCompleted();
    } else {
      // подсказку ПМ читает PmStatusHint из своего стора, сбрасывать надо ее же,
      // иначе текст висит после ухода из статуса
      dispatch(knmPmSetStatusHintText(undefined));
      dispatch(knmPmSetStatusHintFields([]));
      dispatch(knmSetStatusHintText(undefined));
      dispatch(knmSetStatusHintFields([]));
    }
  }, [
    isStatusFillInProcess,
    isProfessionalVisit,
    updateProfessionalVisitTypeCarriedOut,
    updateProfessionalVisitTypeCompleted,
    updateAnnouncementOfWarningRemark,
  ]);
}
